import { useParams, useNavigate } from "react-router-dom";
import PageContainer from "@/components/layout/page-container";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { File as FileIcon, Download, ExternalLink } from "lucide-react";
import {
  useGetOpportunity,
  useGetOpportunityApplication,
} from "@/services/opportunities/queries";
import type { OpportunityApplication } from "@/services/types";

type CustomResponse = NonNullable<
  OpportunityApplication["customResponses"]
>[number];
type CustomResponseFile = NonNullable<CustomResponse["files"]>[number];

export default function OpportunityApplicationDetailPage() {
  const navigate = useNavigate();
  const { id: opportunityId, applicationId } = useParams<{
    id: string;
    applicationId: string;
  }>();

  const { data: opportunityData } = useGetOpportunity(opportunityId || "", {
    enabled: !!opportunityId,
  });

  const { data: applicationData, isLoading } = useGetOpportunityApplication(
    opportunityId || "",
    applicationId || "",
    {
      enabled: !!opportunityId && !!applicationId,
    }
  );

  if (!opportunityId || !applicationId) {
    return (
      <PageContainer scrollable={true}>
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900">
              Application Not Found
            </h2>
            <p className="mt-2 text-gray-600">
              Please select a valid application.
            </p>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer scrollable={true}>
        <div className="max-w-4xl rounded bg-white p-6">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96 mb-8" />
          <div className="space-y-5">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!applicationData) {
    return (
      <PageContainer scrollable={true}>
        <div className="max-w-4xl rounded bg-white p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-900">
              Application not found
            </h2>
            <p className="text-gray-600 mt-2">
              The application you're looking for doesn't exist.
            </p>
            <Button
              onClick={() =>
                navigate(`/dashboard/opportunities/${opportunityId}/applications`)
              }
              className="mt-4"
            >
              Back to Applications
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  const customResponses = applicationData.customResponses || [];

  return (
    <PageContainer scrollable={true}>
      <div className="max-w-4xl rounded bg-white p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold">
              {opportunityData?.title || "Opportunity"} Application
            </h1>
            <p className="mt-1 text-gray-600">
              Submitted{" "}
              {applicationData.createdAt
                ? new Date(applicationData.createdAt).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }
                  )
                : "-"}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              navigate(`/dashboard/opportunities/${opportunityId}/applications`)
            }
          >
            Back to Applications
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-input p-4">
            <p className="text-xs text-muted-foreground">Contact name</p>
            <p className="font-medium text-gray-900">
              {applicationData.contactPersonName || "-"}
            </p>
          </div>
          <div className="rounded-lg border border-input p-4">
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="font-medium text-gray-900">
              {applicationData.contactPersonEmail || "-"}
            </p>
          </div>
          <div className="rounded-lg border border-input p-4">
            <p className="text-xs text-muted-foreground">Phone</p>
            <p className="font-medium text-gray-900">
              {applicationData.contactPersonPhone || "-"}
            </p>
          </div>
          <div className="rounded-lg border border-input p-4">
            <p className="text-xs text-muted-foreground">Status</p>
            <p className="font-medium text-gray-900">
              {applicationData.status || "-"}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Custom Responses</h2>
          {customResponses.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No custom responses submitted.
            </p>
          ) : (
            <div className="space-y-4">
              {customResponses.map((response: CustomResponse, index: number) => (
                <div
                  key={`${response.questionId}-${index}`}
                  className="rounded-lg border border-input p-4"
                >
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    {response.questionLabel}
                  </p>
                  {response.files && response.files.length > 0 ? (
                    <div className="space-y-2">
                      {response.files.map((file: CustomResponseFile, fileIndex: number) => (
                        <div
                          key={file.url}
                          className="flex items-center gap-3 rounded-lg border border-input bg-muted/30 p-3 hover:bg-muted/50 transition-colors"
                        >
                          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                            <FileIcon className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              File {fileIndex + 1} of {response.files?.length}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                              className="h-8"
                            >
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                <span className="text-xs">View</span>
                              </a>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                              className="h-8"
                            >
                              <a
                                href={file.url}
                                download
                                className="flex items-center gap-1"
                              >
                                <Download className="h-3.5 w-3.5" />
                                <span className="text-xs">Download</span>
                              </a>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700">
                      {Array.isArray(response.response)
                        ? response.response.join(", ")
                        : response.response ?? "-"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
